import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, Check, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useUser } from "../context/UserContext";
import { fmtVND, fmtUSD, fmtCurrency } from "../lib/format";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STEP_LABELS = ["Amount", "Destination", "Review"];

export default function Withdraw() {
  const { activeUserId } = useUser();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [amountStr, setAmountStr] = useState("");
  const [payoutCurrency, setPayoutCurrency] = useState("VND");
  const [destId, setDestId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addDestOpen, setAddDestOpen] = useState(false);

  useEffect(() => {
    if (!activeUserId) return;
    Promise.all([api.wallet(activeUserId), api.destinations(activeUserId)]).then(
      ([w, d]) => {
        setWallet(w);
        setDestinations(d);
      }
    );
  }, [activeUserId]);

  const amount = parseInt(amountStr.replace(/\D/g, ""), 10) || 0;

  const amountError = useMemo(() => {
    if (!amount) return null;
    if (amount < 50000) return "Minimum withdrawal is ₫50,000";
    if (wallet && amount > wallet.balance_vnd) return "Amount exceeds available balance";
    return null;
  }, [amount, wallet]);

  const filteredDests = destinations.filter((d) => d.currency === payoutCurrency);

  const selectedDest = destinations.find((d) => d.id === destId);

  const next = async () => {
    if (step === 0) {
      if (!amount || amountError) return;
      setStep(1);
    } else if (step === 1) {
      if (!destId) {
        toast.error("Select or add a destination");
        return;
      }
      // fetch quote
      try {
        const q = await api.quote({
          user_id: activeUserId,
          amount_vnd: amount,
          payout_currency: payoutCurrency,
          destination_id: destId,
        });
        setQuote(q);
        setStep(2);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Quote failed");
      }
    }
  };

  const confirm = async () => {
    setSubmitting(true);
    try {
      const p = await api.withdraw({
        user_id: activeUserId,
        amount_vnd: amount,
        payout_currency: payoutCurrency,
        destination_id: destId,
      });
      toast.success("Withdrawal request submitted");
      nav(`/payouts/${p.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <header className="flex items-center justify-between">
        <button
          onClick={() => (step > 0 ? setStep(step - 1) : nav("/"))}
          data-testid="withdraw-back"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Step <span className="font-semibold text-slate-900">{step + 1}</span> of 3
        </div>
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEP_LABELS.map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                  i < step
                    ? "bg-teal-700 text-white"
                    : i === step
                    ? "bg-teal-700 text-white ring-4 ring-teal-700/15"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className={`text-xs sm:text-sm font-medium ${
                  i <= step ? "text-slate-900" : "text-slate-400"
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className="mx-2 h-px flex-1 bg-slate-200" />
            )}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">
              How much do you want to withdraw?
            </p>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-4xl sm:text-5xl font-bold text-slate-400">
                ₫
              </span>
              <input
                data-testid="withdraw-amount-input"
                autoFocus
                inputMode="numeric"
                value={amountStr ? Number(amountStr.replace(/\D/g, "")).toLocaleString("en-US") : ""}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="0"
                className="flex-1 min-w-0 border-none outline-none bg-transparent font-display font-bold tracking-tighter text-4xl sm:text-6xl text-slate-900 tabular-nums placeholder:text-slate-300 focus:ring-0"
              />
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-500 tabular-nums">
                Available: {fmtVND(wallet?.balance_vnd)}
              </span>
              <button
                type="button"
                onClick={() => setAmountStr(String(wallet?.balance_vnd || 0))}
                data-testid="withdraw-max-btn"
                className="text-teal-700 font-medium hover:text-teal-800"
              >
                Max
              </button>
            </div>
            {amountError && (
              <p data-testid="withdraw-amount-error" className="mt-2 text-sm text-red-600">
                {amountError}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {[100000, 250000, 500000, 1000000].map((v) => (
              <button
                key={v}
                onClick={() => setAmountStr(String(v))}
                data-testid={`quick-amount-${v}`}
                className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-700 hover:border-teal-700 hover:text-teal-700 transition-colors tabular-nums"
              >
                {fmtVND(v)}
              </button>
            ))}
          </div>

          <button
            data-testid="withdraw-continue"
            onClick={next}
            disabled={!amount || !!amountError}
            className="w-full rounded-full bg-teal-700 py-4 text-base font-semibold text-white transition-colors hover:bg-teal-800 disabled:bg-slate-300"
          >
            Continue
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">
              Payout currency
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {["VND", "USD"].map((c) => (
                <button
                  key={c}
                  data-testid={`currency-option-${c}`}
                  onClick={() => {
                    setPayoutCurrency(c);
                    setDestId(null);
                  }}
                  className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                    payoutCurrency === c
                      ? "border-teal-700 bg-teal-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="font-display text-lg font-bold text-slate-900">
                    {c}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {c === "VND" ? "Local (no FX)" : "Cross-border · 1 USD = ₫26,000"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">
                Destination
              </Label>
              <button
                onClick={() => setAddDestOpen(true)}
                data-testid="add-destination-btn"
                className="flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
              >
                <Plus className="h-3.5 w-3.5" /> Add new
              </button>
            </div>
            <div className="space-y-2">
              {filteredDests.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500 text-center">
                  No {payoutCurrency} destinations. Add one to continue.
                </div>
              )}
              {filteredDests.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDestId(d.id)}
                  data-testid={`destination-${d.id}`}
                  className={`w-full flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${
                    destId === d.id
                      ? "border-teal-700 bg-teal-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{d.label}</div>
                    <div className="text-xs text-slate-500">
                      {d.bank_name} • ••••{d.account_number.slice(-4)}
                    </div>
                  </div>
                  <span className="text-xs font-semibold rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                    {d.currency}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="withdraw-review"
            onClick={next}
            disabled={!destId}
            className="w-full rounded-full bg-teal-700 py-4 text-base font-semibold text-white transition-colors hover:bg-teal-800 disabled:bg-slate-300"
          >
            Review
          </button>
        </div>
      )}

      {step === 2 && quote && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 space-y-6">
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Review withdrawal
          </h2>

          <div className="rounded-2xl bg-slate-50 p-6 space-y-3">
            <Row label="You send" value={fmtVND(quote.amount_vnd)} big />
            <Row
              label={`Mock fee (0.5% + ${
                payoutCurrency === "VND" ? "₫10,000" : "$0.40"
              })`}
              value={`- ${fmtVND(quote.fee_vnd)}`}
              muted
            />
            {payoutCurrency === "USD" && (
              <Row
                label="Mock FX rate"
                value="1 USD = ₫26,000"
                muted
              />
            )}
            <div className="my-2 h-px bg-slate-200" />
            <Row
              label="Net payout"
              value={fmtCurrency(quote.net_amount, payoutCurrency)}
              accent
              big
              testId="review-net"
            />
            <Row label="Estimated arrival" value={quote.estimated_arrival} muted />
          </div>

          {selectedDest && (
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-widest text-slate-500">
                To
              </p>
              <p className="mt-1 font-medium text-slate-900">{selectedDest.label}</p>
              <p className="text-xs text-slate-500">
                {selectedDest.bank_name} • {selectedDest.account_holder} • ••••
                {selectedDest.account_number.slice(-4)}
              </p>
            </div>
          )}

          <button
            data-testid="withdraw-confirm"
            onClick={confirm}
            disabled={submitting}
            className="w-full rounded-full bg-orange-500 py-4 text-base font-bold text-white shadow-lg shadow-orange-500/20 transition-colors hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <>
                Confirm withdrawal <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-500">
            You&apos;ll be able to track this payout in real-time.
          </p>
        </div>
      )}

      <AddDestinationDialog
        open={addDestOpen}
        onOpenChange={setAddDestOpen}
        defaultCurrency={payoutCurrency}
        onAdded={(d) => {
          setDestinations((prev) => [...prev, d]);
          setPayoutCurrency(d.currency);
          setDestId(d.id);
        }}
      />
    </div>
  );
}

const Row = ({ label, value, big, muted, accent, testId }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className={`text-sm ${muted ? "text-slate-500" : "text-slate-700"}`}>
      {label}
    </span>
    <span
      data-testid={testId}
      className={`tabular-nums ${
        big ? "font-display text-2xl sm:text-3xl font-bold" : "text-sm font-medium"
      } ${accent ? "text-teal-700" : muted ? "text-slate-500" : "text-slate-900"}`}
    >
      {value}
    </span>
  </div>
);

function AddDestinationDialog({ open, onOpenChange, defaultCurrency, onAdded }) {
  const { activeUserId } = useUser();
  const [form, setForm] = useState({
    label: "",
    currency: defaultCurrency,
    bank_name: "",
    account_number: "",
    account_holder: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, currency: defaultCurrency }));
  }, [open, defaultCurrency]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.addDestination(activeUserId, form);
      toast.success("Destination added");
      onAdded(d);
      onOpenChange(false);
      setForm({ label: "", currency: defaultCurrency, bank_name: "", account_number: "", account_holder: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add destination");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-destination-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add a destination</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              data-testid="dest-label"
              placeholder="e.g., Vietcombank Savings"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="dest-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VND">VND</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bank name</Label>
            <Input
              data-testid="dest-bank"
              placeholder="e.g., Vietcombank"
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Account holder</Label>
            <Input
              data-testid="dest-holder"
              placeholder="Full name"
              value={form.account_holder}
              onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Account number</Label>
            <Input
              data-testid="dest-account"
              placeholder="0011-234567-890"
              value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="dest-save"
            onClick={save}
            disabled={saving || !form.label || !form.bank_name || !form.account_number}
            className="rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:bg-slate-300"
          >
            {saving ? "Saving…" : "Save destination"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
